import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import propertyService from '../../services/propertyService';
import AuthService from '../../services/authService';
import PropertyDetailsModal from '../common/PropertyDetailsModal';
import LeadClaimModal from './LeadClaimModal';
import PointsPurchaseModal from './PointsPurchaseModal';
import SupportChat from '../common/SupportChat';
import darkIcon from '../../assets/dark-iconp.png';
import { Bell, Plus, Mail, Lock, X, Eye, EyeOff, Calendar, FileText, PlusCircle, HelpCircle, Upload, ZoomIn, ZoomOut, Check } from 'react-feather';
import Cropper from 'react-easy-crop';
import '../admin/AdminDashboard.css';
import './AgentDashboard.css';

const AgentDashboard = ({ agentInfo, propertyCount, onAvailableLeads, onInvitations, onShowings }) => {
  const [searchParams] = useSearchParams();
  const [recentProperties, setRecentProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [today] = useState(new Date());
  const [inReviewCount, setInReviewCount] = useState(0);
  const [agentData, setAgentData] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [showSupportChat, setShowSupportChat] = useState(false);
  const [lockoutDismissed, setLockoutDismissed] = useState(false); // lets a cancelled agent bypass the lockout to export their data
  const [assignedBuyersCount, setAssignedBuyersCount] = useState(0);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableUsersCount, setAvailableUsersCount] = useState(0);
  const [isLoadingAvailableUsers, setIsLoadingAvailableUsers] = useState(true);
  const [matchesSentCount, setMatchesSentCount] = useState(0);
  const [invitedUsersCount, setInvitedUsersCount] = useState(0);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = React.useRef(null);

  // Demo accounts browse the pool by state rather than by assignment, so the
  // decline badge doesn't mean the same thing for them — matches AvailableLeads.
  const isDemoAccount = !!(
    agentData?.agentInfo?.isDemoAccount || agentInfo?.agentInfo?.isDemoAccount
  );
  
  // Cropper state
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  useEffect(() => {
    fetchRecentProperties();
    fetchInReviewProperties();
    fetchAgentData();
    fetchAssignedBuyersCount();
    fetchAvailableUsers();
    fetchMatchesSentCount();
    fetchInvitedUsersCount();
  }, [agentInfo]);

  // Debug the agent info
  useEffect(() => {
    console.log('Agent info from props:', agentInfo);
  }, [agentInfo]);

  // Check for leadId in URL parameter (from email link)
  useEffect(() => {
    const leadId = searchParams.get('leadId');
    if (leadId) {
      setSelectedLeadId(leadId);
      setShowLeadModal(true);
    }
  }, [searchParams]);

  
  const fetchAgentData = async () => {
    if (!agentInfo || !agentInfo._id) return;
    
    try {
      const response = await fetch('https://back-end-x7tx.onrender.com/api/users/profile', {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched agent data:', data);
        if (data.success && data.data) {
          setAgentData(data.data);
        }
      } else {
        console.error('Failed to fetch agent data');
      }
    } catch (error) {
      console.error('Error fetching agent data:', error);
    }
  };

  const fetchAssignedBuyersCount = async () => {
    if (!agentInfo || !agentInfo._id) return;
    
    try {
      const response = await fetch(`https://back-end-x7tx.onrender.com/api/users/agent/${agentInfo._id}/buyers`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setAssignedBuyersCount(data.data.length);
        }
      } else {
        console.error('Failed to fetch assigned buyers count');
      }
    } catch (error) {
      console.error('Error fetching assigned buyers count:', error);
    }
  };

  const fetchAvailableUsers = async () => {
    if (!agentInfo || !agentInfo._id) return;
    
    setIsLoadingAvailableUsers(true);
    try {
      const response = await fetch('https://back-end-x7tx.onrender.com/api/agent-pools/available-leads', {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          // Store full count for stat widget
          setAvailableUsersCount(data.data.length);
          // Limit to 7 most recent available users for display
          setAvailableUsers(data.data.slice(0, 7));
        }
      } else {
        console.error('Failed to fetch available users');
      }
    } catch (error) {
      console.error('Error fetching available users:', error);
    } finally {
      setIsLoadingAvailableUsers(false);
    }
  };

  const fetchMatchesSentCount = async () => {
    if (!agentInfo || !agentInfo._id) return;
    
    try {
      const response = await fetch('https://back-end-x7tx.onrender.com/api/curated-matches', {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setMatchesSentCount(data.data.length);
        }
      } else {
        console.error('Failed to fetch matches sent count');
      }
    } catch (error) {
      console.error('Error fetching matches sent count:', error);
    }
  };

  const fetchInvitedUsersCount = async () => {
    if (!agentInfo || !agentInfo._id) return;
    
    try {
      const response = await fetch('https://back-end-x7tx.onrender.com/api/invitations', {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setInvitedUsersCount(data.data.length);
        }
      } else {
        console.error('Failed to fetch invited users count');
      }
    } catch (error) {
      console.error('Error fetching invited users count:', error);
    }
  };

  const validatePassword = (password) => {
    if (password.length < 5) {
      return 'Password must be at least 5 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must include at least one uppercase letter';
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      return 'Password must include at least one symbol';
    }
    return null;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Reset messages
    setPasswordError('');
    setPasswordSuccess('');
    
    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    // Validate password requirements
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setPasswordError(passwordError);
      return;
    }
    
    setIsChangingPassword(true);
    
    try {
      const response = await AuthService.changePassword(currentPassword, newPassword);
      
      if (response.success) {
        setPasswordSuccess('Password changed successfully');
        // Reset form
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        
        // Close modal after short delay
        setTimeout(() => {
          setShowPasswordModal(false);
          setPasswordSuccess('');
        }, 2000);
      } else {
        setPasswordError(response.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordError(error.message || 'An error occurred while changing password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const fetchRecentProperties = async () => {
    try {
      if (!agentInfo || !agentInfo._id) return;
      
      setIsLoading(true);
      
      // Fetch both approved and in-review properties
      const [approvedRes, inReviewRes] = await Promise.all([
        propertyService.getAgentProperties(agentInfo._id, { 
          limit: 5, 
          sort: '-createdAt' 
        }),
        propertyService.getPendingProperties({
          agent: agentInfo._id
        })
      ]);
      
      const approved = approvedRes?.data || [];
      
      // Mark in-review properties with an explicit flag
      const inReview = (inReviewRes?.data || []).map(property => ({
        ...property,
        isInReview: true  // Add this flag to identify in-review properties
      }));
      
      // Combine and sort properties by creation date
      const allProperties = [...approved, ...inReview].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      ).slice(0, 5); // Limit to 5 most recent
      
      setRecentProperties(allProperties);
    } catch (error) {
      console.error('Error fetching recent properties:', error);
      setError('Failed to load recent properties');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInReviewProperties = async () => {
    try {
      if (!agentInfo || !agentInfo._id) return;
      
      // Fetch properties that are in review
      const response = await propertyService.getPendingProperties({
        agent: agentInfo._id
      });
      
      if (response && response.data) {
        setInReviewCount(response.data.length);
      }
    } catch (error) {
      console.error('Error fetching in-review properties:', error);
    }
  };

  const handleEmailRequest = () => {
    // Create mailto link to request updates
    const subject = encodeURIComponent('Request to Update Agent Information');
    const body = encodeURIComponent(
      `Hello,\n\nI would like to request an update to my agent information:\n\n` +
      `Current Information:\n` +
      `Agent Name: ${agentData?.name || agentInfo?.name}\n` +
      `Agent ID: ${agentData?._id || agentInfo?._id}\n` +
      `Email: ${agentData?.email || agentInfo?.email}\n` +
      `Phone: ${agentData?.phone || agentInfo?.phone}\n` +
      `Brokerage: ${agentData?.agentInfo?.agency || agentInfo?.agentInfo?.agency || agentInfo?.agency || 'Not provided'}\n` +
      `License Number: ${agentData?.agentInfo?.licenseNumber || agentInfo?.agentInfo?.licenseNumber || 'Not provided'}\n\n` +
      `Requested Changes:\n` +
      `Name: [Your New Name]\n` +
      `Email: [Your New Email]\n` +
      `Phone: [Your New Phone]\n` +
      `Brokerage: [Your New Brokerage]\n` +
      `License Number: [Your New License Number]\n\n` +
      `Thank you.`
    );
    
    window.location.href = `mailto:team@lauft.app?subject=${subject}&body=${body}`;
  };

  // ===== Cancelled-account lockout actions =====
  // Both open a pre-filled email to team@lauft.app. "Get Support" is
  // intentionally the same channel as reactivation for now — different subject
  // so the team can triage which one it is.
  const handleReactivateAccount = () => {
    const subject = encodeURIComponent('Reactivate Agent Account Request');
    const body = encodeURIComponent(
      `Hello,\n\nI would like to reactivate my Lauft agent account.\n\n` +
      `Account Details:\n` +
      `Agent Name: ${agentData?.name || agentInfo?.name || 'N/A'}\n` +
      `Agent ID: ${agentData?._id || agentInfo?._id || 'N/A'}\n` +
      `Email: ${agentData?.email || agentInfo?.email || 'N/A'}\n` +
      `Phone: ${agentData?.phone || agentInfo?.phone || 'N/A'}\n\n` +
      `Please let me know what's needed to restore my account.\n\nThank you.`
    );
    window.location.href = `mailto:team@lauft.app?subject=${subject}&body=${body}`;
  };

  const handleCancelledSupport = () => {
    const subject = encodeURIComponent('Support Request - Cancelled Account');
    const body = encodeURIComponent(
      `Hello,\n\nI need help with my cancelled Lauft agent account.\n\n` +
      `Account Details:\n` +
      `Agent Name: ${agentData?.name || agentInfo?.name || 'N/A'}\n` +
      `Agent ID: ${agentData?._id || agentInfo?._id || 'N/A'}\n` +
      `Email: ${agentData?.email || agentInfo?.email || 'N/A'}\n\n` +
      `My question:\n[Describe your issue here]\n\nThank you.`
    );
    window.location.href = `mailto:team@lauft.app?subject=${subject}&body=${body}`;
  };

  // Photo upload handlers
  const handlePhotoClick = () => {
    setShowPhotoModal(true);
    setPhotoError('');
    setPhotoPreview(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const processFile = (file) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setPhotoError('Please select an image file (JPG, PNG, etc.)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setPhotoError('Image must be less than 10MB');
      return;
    }

    setPhotoError('');
    
    // Create image URL for cropper
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageToCrop(e.target.result);
      setShowCropper(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  // Cropper callback when crop area changes
  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  // Create cropped image from canvas
  const createCroppedImage = async (imageSrc, pixelCrop) => {
    const image = new Image();
    image.src = imageSrc;
    
    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Set canvas size to the cropped area
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Draw the cropped image
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    // Convert to blob
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.95);
    });
  };

  // Handle crop confirmation
  const handleCropConfirm = async () => {
    try {
      const croppedBlob = await createCroppedImage(imageToCrop, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], 'profile-photo.jpg', { type: 'image/jpeg' });
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(croppedBlob);
      
      setPhotoPreview({
        file: croppedFile,
        dataUrl: previewUrl
      });
      setShowCropper(false);
      setImageToCrop(null);
    } catch (error) {
      console.error('Error cropping image:', error);
      setPhotoError('Failed to crop image. Please try again.');
    }
  };

  // Handle crop cancel
  const handleCropCancel = () => {
    setShowCropper(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handlePhotoUpload = async () => {
    if (!photoPreview?.file) {
      setPhotoError('Please select an image first');
      return;
    }

    setIsUploadingPhoto(true);
    setPhotoError('');

    try {
      // Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', photoPreview.file);
      formData.append('upload_preset', 'lauft_profiles'); // You may need to create this preset
      formData.append('folder', 'profiles');

      const cloudinaryResponse = await fetch(
        'https://api.cloudinary.com/v1_1/dckv4itgt/image/upload',
        {
          method: 'POST',
          body: formData
        }
      );

      if (!cloudinaryResponse.ok) {
        throw new Error('Failed to upload image to cloud storage');
      }

      const cloudinaryData = await cloudinaryResponse.json();
      const imageUrl = cloudinaryData.secure_url;

      console.log('Image uploaded to Cloudinary:', imageUrl);

      // Update backend with new profile image URL
      const response = await fetch('https://back-end-x7tx.onrender.com/api/users/profile/image', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ profileImage: imageUrl })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update profile image');
      }

      // Update local state
      setAgentData(prev => ({
        ...prev,
        profileImage: imageUrl
      }));

      // Close modal and reset
      setShowPhotoModal(false);
      setPhotoPreview(null);
      
      console.log('Profile image updated successfully');

    } catch (error) {
      console.error('Error uploading profile image:', error);
      setPhotoError(error.message || 'Failed to upload image. Please try again.');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const closePhotoModal = () => {
    setShowPhotoModal(false);
    setPhotoPreview(null);
    setPhotoError('');
    setIsDragging(false);
    // Reset cropper state
    setShowCropper(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const openPropertyDetails = (property) => {
    setSelectedProperty(property);
    setShowDetailsModal(true);
  };

  const closePropertyDetails = () => {
    setShowDetailsModal(false);
    setSelectedProperty(null);
  };

  // Calculate statistics
  const calculateStats = () => {
    if (!recentProperties.length) return { forSale: 0, forRent: 0 };
    
    return recentProperties.reduce((stats, property) => {
      // Count only properties that are not in review
      if (!property.isInReview) {
        if (property.listingType === 'purchase' && property.propertyStatus === 'For Sale') {
          stats.forSale++;
        } else if (property.listingType === 'rent') {
          stats.forRent++;
        }
      }
      return stats;
    }, { forSale: 0, forRent: 0 });
  };

  const stats = calculateStats();
  
  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // Format date to display with better styling
  const formattedDate = today.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  // Format US phone numbers to (xxx) xxx-xxxx. Returns original string if
  // we don't recognize the shape so we never mangle international numbers.
  const formatPhone = (raw) => {
    if (!raw) return 'Not provided';
    const digits = String(raw).replace(/\D/g, '');
    // 10-digit US number
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    // 11-digit starting with country code 1
    if (digits.length === 11 && digits.startsWith('1')) {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    // Anything else — return as-entered
    return raw;
  };

  // Dashboard Content
  const dashboardContent = (
    <div style={{ color: '#1a1a1a' }}>
      <style>{`
        /* Enhanced Greeting Styles */
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .greeting {
          animation: fadeInUp 0.6s ease-out;
          text-align: left;
        }
        .greeting h1 {
          font-size: 2.5rem !important;
          font-weight: 700 !important;
          margin: 0 0 8px 0 !important;
          line-height: 1.2 !important;
          color: #1a1a1a !important;
          letter-spacing: -0.02em !important;
          text-align: left !important;
        }
        .greeting-text {
          font-size: 1.5rem;
          font-weight: 400;
          color: #1a1a1a;
        }
        .greeting p {
          font-size: 0.95rem !important;
          color: rgba(26, 26, 26, 0.55) !important;
          margin: 0 !important;
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          letter-spacing: 0.3px;
        }
        .date-icon {
          opacity: 0.55;
          color: rgba(26, 26, 26, 0.55);
        }
        /* Light mode button hover states */
        .add-property-btn:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
          border-color: rgba(212, 168, 0, 0.35) !important;
          background-color: #ffffff !important;
          color: #1a1a1a !important;
        }
        .notification-btn:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
          border-color: rgba(212, 168, 0, 0.35) !important;
          background-color: #ffffff !important;
          color: #1a1a1a !important;
        }
        .modern-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
        }
        .header-left {
          display: flex;
          align-items: center;
          gap: 20px;
        }
        .profile-photo {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid #ffffff;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18), 0 2px 6px rgba(0, 0, 0, 0.1);
        }
        .profile-placeholder-circle {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: #f4f4f5;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px solid #ffffff;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18), 0 2px 6px rgba(0, 0, 0, 0.1);
        }
        .profile-initials-small {
          font-size: 1.5rem;
          font-weight: 700;
          color: rgba(26, 26, 26, 0.5);
        }
        .profile-photo-container {
          position: relative;
          cursor: pointer;
          transition: transform 0.2s ease;
        }
        .profile-photo-container:hover {
          transform: scale(1.05);
        }
        @keyframes photoModalIn {
          from {
            opacity: 0;
            transform: translateY(8px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes photoPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.06); }
        }
        .photo-upload-modal {
          max-width: 460px;
          width: 90%;
          background: #ffffff;
          border: 1px solid rgba(26, 26, 26, 0.06);
          box-shadow: 0 24px 64px rgba(0, 0, 0, 0.18), 0 4px 12px rgba(0, 0, 0, 0.06);
          border-radius: 20px;
          padding: 28px;
          animation: photoModalIn 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);
        }
        .photo-modal-title {
          color: #1a1a1a;
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 1.2rem;
          font-weight: 700;
          letter-spacing: -0.01em;
          margin: 0;
        }
        .photo-modal-title::before {
          content: '';
          display: inline-block;
          width: 28px;
          height: 28px;
          background: rgba(26, 26, 26, 0.06);
          border-radius: 8px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%231a1a1a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z'%3E%3C/path%3E%3Ccircle cx='12' cy='13' r='4'%3E%3C/circle%3E%3C/svg%3E");
          background-size: 16px 16px;
          background-repeat: no-repeat;
          background-position: center;
        }
        .photo-drop-zone {
          border: 2px dashed rgba(26, 26, 26, 0.15);
          border-radius: 14px;
          padding: 48px 24px;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s ease;
          background: rgba(26, 26, 26, 0.02);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .photo-drop-zone:hover,
        .photo-drop-zone.dragging {
          border-color: rgba(26, 26, 26, 0.35);
          background: rgba(26, 26, 26, 0.04);
          transform: translateY(-1px);
        }
        .photo-drop-zone.dragging .photo-drop-zone-icon {
          animation: photoPulse 0.9s ease-in-out infinite;
        }
        .photo-drop-zone-icon-wrap {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: rgba(26, 26, 26, 0.06);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          transition: background 0.2s ease;
        }
        .photo-drop-zone:hover .photo-drop-zone-icon-wrap,
        .photo-drop-zone.dragging .photo-drop-zone-icon-wrap {
          background: rgba(26, 26, 26, 0.1);
        }
        .photo-drop-zone-icon {
          color: rgba(26, 26, 26, 0.65);
        }
        .photo-drop-zone-text {
          color: #1a1a1a;
          margin: 0 0 6px 0;
          font-size: 16px;
          font-weight: 600;
        }
        .photo-drop-zone-subtext {
          color: rgba(26, 26, 26, 0.55);
          margin: 0;
          font-size: 13px;
        }
        .photo-drop-zone-hint {
          color: rgba(26, 26, 26, 0.4);
          margin: 18px 0 0 0;
          font-size: 12px;
          padding: 6px 12px;
          background: rgba(26, 26, 26, 0.04);
          border-radius: 999px;
          display: inline-block;
        }
        .photo-preview-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          padding: 20px 0 8px;
        }
        .photo-preview-wrap {
          position: relative;
          display: inline-block;
        }
        .photo-preview {
          width: 180px;
          height: 180px;
          border-radius: 50%;
          object-fit: cover;
          border: 4px solid #ffffff;
          box-shadow: 0 10px 32px rgba(0, 0, 0, 0.18), 0 2px 6px rgba(0, 0, 0, 0.08);
        }
        .photo-preview-check {
          position: absolute;
          bottom: 4px;
          right: 4px;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #1a1a1a;
          border: 3px solid #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
        }
        .btn-change-photo {
          padding: 8px 18px;
          background: transparent;
          color: rgba(26, 26, 26, 0.75);
          border: 1px solid rgba(26, 26, 26, 0.12);
          border-radius: 999px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          transition: all 0.15s ease;
        }
        .btn-change-photo:hover {
          background: rgba(26, 26, 26, 0.04);
          border-color: rgba(26, 26, 26, 0.25);
          color: #1a1a1a;
        }
        .photo-actions {
          display: flex;
          gap: 10px;
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid rgba(26, 26, 26, 0.06);
        }
        .btn-upload {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 11px 22px;
          background: #1a1a1a;
          color: #ffffff;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.15s ease;
          min-width: 130px;
          letter-spacing: -0.005em;
        }
        .btn-upload:hover:not(:disabled) {
          background: #000000;
          transform: translateY(-1px);
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
        }
        .btn-upload:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .photo-modal-cancel {
          background: #ffffff;
          border: 1px solid rgba(26, 26, 26, 0.12);
          color: rgba(26, 26, 26, 0.8);
          padding: 11px 22px;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.15s ease;
        }
        .photo-modal-cancel:hover {
          background: rgba(26, 26, 26, 0.04);
          border-color: rgba(26, 26, 26, 0.25);
          color: #1a1a1a;
        }
        /* Cropper styling */
        .cropper-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .cropper-wrapper {
          position: relative;
          width: 100%;
          height: 320px;
          background: #f4f4f5;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid rgba(26, 26, 26, 0.06);
        }
        .cropper-controls {
          padding: 4px 2px;
        }
        .zoom-control {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .zoom-icon {
          color: rgba(26, 26, 26, 0.5);
          flex-shrink: 0;
        }
        .zoom-slider {
          flex: 1;
          -webkit-appearance: none;
          appearance: none;
          height: 4px;
          border-radius: 999px;
          background: rgba(26, 26, 26, 0.1);
          outline: none;
          cursor: pointer;
        }
        .zoom-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #1a1a1a;
          border: 2px solid #ffffff;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
          transition: transform 0.15s ease;
        }
        .zoom-slider::-webkit-slider-thumb:hover {
          transform: scale(1.15);
        }
        .zoom-slider::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #1a1a1a;
          border: 2px solid #ffffff;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
        }
        .stat-widgets {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 32px;
        }
        .stat-widget {
          background-color: #ffffff;
          border: 1px solid rgba(26, 26, 26, 0.08);
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          transition: all 0.2s ease;
          position: relative;
          overflow: hidden;
        }
        .stat-widget:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
          border-color: rgba(212, 168, 0, 0.35);
        }
        .stat-widget-icon {
          position: absolute;
          top: 20px;
          right: 20px;
          opacity: 0.35;
        }
        .stat-number {
          font-size: 2.5rem;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 8px;
          position: relative;
          z-index: 1;
          letter-spacing: -0.02em;
        }
        .stat-label {
          font-size: 0.75rem;
          color: rgba(26, 26, 26, 0.55);
          text-transform: uppercase;
          letter-spacing: 0.06em;
          font-weight: 600;
        }
        .main-content-grid {
          display: grid;
          grid-template-columns: 70% 30%;
          gap: 24px;
        }
        @media (max-width: 1024px) {
          .main-content-grid {
            grid-template-columns: 1fr;
          }
        }
        .quick-actions-panel {
          background-color: #ffffff;
          border: 1px solid rgba(26, 26, 26, 0.08);
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          height: fit-content;
        }
        .quick-actions-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1a1a1a;
          margin: -24px -24px 20px -24px;
          padding: 18px 24px;
          background: rgba(253, 215, 0, 0.08);
          border-radius: 12px 12px 0 0;
          text-align: center;
          letter-spacing: -0.01em;
        }
        .quick-action-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 14px 18px;
          background-color: rgba(253, 215, 0, 0.08);
          color: #1a1a1a;
          border: 1px solid rgba(253, 215, 0, 0.2);
          border-radius: 10px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.15s ease;
          margin-bottom: 10px;
        }
        .quick-action-btn:hover {
          background-color: rgba(253, 215, 0, 0.18);
          border-color: rgba(212, 168, 0, 0.5);
          color: #1a1a1a;
          transform: translateY(-1px);
        }
        .agent-info-compact {
          background-color: #ffffff;
          border: 1px solid rgba(26, 26, 26, 0.08);
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          margin-top: 20px;
        }
        .agent-info-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          padding-bottom: 12px;
          border-bottom: 1px solid rgba(26, 26, 26, 0.08);
        }
        .agent-info-expanded {
          margin-top: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .agent-info-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .agent-info-label {
          font-size: 0.7rem;
          color: rgba(26, 26, 26, 0.5);
          text-transform: uppercase;
          letter-spacing: 0.06em;
          font-weight: 600;
        }
        .agent-info-value {
          color: #1a1a1a;
          font-weight: 500;
          font-size: 0.9rem;
        }
        .property-card, .info-card, .recent-properties {
          background-color: #ffffff !important;
          border: 1px solid rgba(26, 26, 26, 0.08) !important;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
          border-radius: 12px !important;
          padding: 24px !important;
        }
        .section-title, .info-card-title, .card-title {
          color: #1a1a1a !important;
          border-bottom: none !important;
          border: none !important;
          background: rgba(253, 215, 0, 0.08) !important;
          font-weight: 700 !important;
          letter-spacing: -0.01em !important;
          margin: -24px -24px 20px -24px !important;
          padding: 18px 24px !important;
          border-radius: 12px 12px 0 0 !important;
          text-align: center !important;
        }
        .section-title::after, .section-title::before {
          display: none !important;
        }
        .status-badge {
          display: inline-block !important;
          padding: 0.25rem 0.5rem !important;
          border-radius: 0.25rem !important;
          font-size: 0.875rem !important;
          font-weight: 500 !important;
        }
        .status-pending {
          background-color: rgba(234, 88, 12, 0.1) !important;
          color: #c2410c !important;
          border: 1px solid rgba(234, 88, 12, 0.25) !important;
        }
        .status-approved {
          background-color: rgba(5, 150, 105, 0.1) !important;
          color: #047857 !important;
          border: 1px solid rgba(5, 150, 105, 0.25) !important;
        }
        .properties-table {
          width: 100%;
          border-collapse: collapse;
        }
        .properties-table thead tr {
          background: rgba(253, 215, 0, 0.08) !important;
        }
        .properties-table th {
          color: rgba(26, 26, 26, 0.7) !important;
          border-bottom: 1px solid rgba(253, 215, 0, 0.2) !important;
          font-weight: 700 !important;
          font-size: 0.78rem !important;
          letter-spacing: -0.005em !important;
          padding: 14px 16px !important;
          text-align: left !important;
        }
        .properties-table td {
          color: #1a1a1a !important;
          border-bottom: 1px solid rgba(26, 26, 26, 0.06) !important;
          padding: 14px 16px !important;
        }
        .properties-table tr:last-child td {
          border-bottom: none !important;
        }
        .properties-table tbody tr {
          transition: background-color 0.15s ease;
        }
        .properties-table tbody tr:hover {
          background-color: rgba(253, 215, 0, 0.06) !important;
        }
        .status-cell {
          min-width: 120px;
          white-space: nowrap;
        }
        .property-address {
          color: #1a1a1a !important;
          transition: color 0.2s;
          font-weight: 600;
        }
        .property-address:hover {
          color: #1a1a1a !important;
        }
        .property-neighborhood {
          color: rgba(26, 26, 26, 0.5) !important;
          font-size: 0.85rem !important;
        }
        .loading-state p, .empty-state p {
          color: rgba(26, 26, 26, 0.6) !important;
        }
        .empty-state {
          text-align: center;
          padding: 60px 20px;
          background-color: rgba(26, 26, 26, 0.02);
          border-radius: 8px;
        }
        .add-first-property-btn {
          margin-top: 20px;
          padding: 12px 32px;
          background-color: #ffffff;
          border: 1px solid rgba(26, 26, 26, 0.15);
          border-radius: 8px;
          color: #1a1a1a;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .add-first-property-btn:hover {
          background-color: rgba(253, 215, 0, 0.1);
          border-color: rgba(212, 168, 0, 0.4);
          color: #a87f00;
          transform: translateY(-1px);
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .spinner {
          border: 3px solid rgba(26, 26, 26, 0.08) !important;
          border-top-color: #d4a800 !important;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          animation: spin 0.7s linear infinite;
          display: inline-block;
        }
        .spinner.spinner-light {
          border-color: rgba(255, 255, 255, 0.25) !important;
          border-top-color: #ffffff !important;
        }
        /* Skeleton loading */
        @keyframes skeleton-shimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        .skeleton-bar {
          background: linear-gradient(
            90deg,
            rgba(26, 26, 26, 0.05) 0%,
            rgba(26, 26, 26, 0.1) 50%,
            rgba(26, 26, 26, 0.05) 100%
          );
          background-size: 800px 100%;
          animation: skeleton-shimmer 1.4s ease-in-out infinite;
          border-radius: 6px;
          height: 14px;
          display: inline-block;
          vertical-align: middle;
        }
        /* Alert styles */
        .alert {
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 0.9rem;
          margin-bottom: 16px;
        }
        .alert-danger, .alert-error {
          background-color: rgba(220, 38, 38, 0.08);
          color: #b91c1c;
          border: 1px solid rgba(220, 38, 38, 0.2);
        }
        .alert-success {
          background-color: rgba(5, 150, 105, 0.08);
          color: #047857;
          border: 1px solid rgba(5, 150, 105, 0.2);
        }
        /* Modal styles (password modal, etc.) */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(26, 26, 26, 0.4);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }
        .modal-content {
          background: #ffffff;
          border: 1px solid rgba(26, 26, 26, 0.08);
          border-radius: 16px;
          padding: 28px;
          max-width: 500px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        .modal-header h3 {
          color: #1a1a1a;
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0;
        }
        .btn-close {
          background: none;
          border: none;
          color: rgba(26, 26, 26, 0.5);
          cursor: pointer;
          padding: 4px;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .btn-close:hover {
          background: rgba(26, 26, 26, 0.06);
          color: #1a1a1a;
        }
        .form-group {
          margin-bottom: 16px;
        }
        .form-group label {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 6px;
        }
        .form-group input {
          width: 100%;
          padding: 10px 12px;
          background: #ffffff;
          border: 1px solid rgba(26, 26, 26, 0.15);
          border-radius: 8px;
          color: #1a1a1a;
          font-size: 0.95rem;
          transition: border-color 0.15s ease;
        }
        .form-group input:focus {
          outline: none;
          border-color: #d4a800;
          box-shadow: 0 0 0 3px rgba(253, 215, 0, 0.2);
        }
        .password-input-container {
          position: relative;
        }
        .toggle-password-visibility {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: rgba(26, 26, 26, 0.5);
          cursor: pointer;
          padding: 4px;
        }
        .toggle-password-visibility:hover {
          color: #1a1a1a;
        }
        .password-requirements {
          font-size: 0.8rem;
          margin-top: 6px;
          color: rgba(26, 26, 26, 0.55);
        }
        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 20px;
        }
        .btn-cancel {
          background: #ffffff;
          border: 1px solid rgba(26, 26, 26, 0.15);
          color: rgba(26, 26, 26, 0.8);
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .btn-cancel:hover {
          background: rgba(26, 26, 26, 0.04);
          border-color: rgba(26, 26, 26, 0.25);
        }
        .btn-submit {
          background: #fdd700;
          color: #1a1a1a;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .btn-submit:hover:not(:disabled) {
          background: #e5c200;
          transform: translateY(-1px);
        }
        .btn-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>

      {/* Modern Header with Profile Photo */}
      <div className="modern-header">
        <div className="header-left">
          <div className="profile-photo-container" onClick={handlePhotoClick} title="Click to change profile photo">
            {(agentData?.profileImage && agentData.profileImage !== 'default-profile.jpg') ? (
              <img 
                src={agentData.profileImage} 
                alt="Profile" 
                className="profile-photo"
              />
            ) : (
              <div className="profile-placeholder-circle">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#999999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
            )}
          </div>
          <div className="greeting">
            <h1>
              <span className="greeting-text">{getGreeting()},</span>
            </h1>
            <p>
              <svg className="date-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              {formattedDate}
            </p>
          </div>
        </div>
        
        <div className="header-actions">
          <button 
            className="notification-btn"
            style={{
              backgroundColor: '#ffffff',
              color: '#1a1a1a',
              border: '1px solid rgba(26, 26, 26, 0.1)',
              borderRadius: '10px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              minWidth: '120px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1px',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
            onClick={() => setShowPointsModal(true)}
          >
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a1a' }}>
              {agentData?.pointsBalance ?? agentInfo?.pointsBalance ?? 0} Points
            </div>
            <div style={{ fontSize: '10px', opacity: '0.6', fontWeight: '500', color: 'rgba(26, 26, 26, 0.7)' }}>
              Add Points
            </div>
          </button>
          <button 
            className="add-property-btn"
            style={{
              backgroundColor: '#ffffff',
              color: '#1a1a1a',
              border: '1px solid rgba(26, 26, 26, 0.1)',
              borderRadius: '10px',
              padding: '10px 18px',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              boxShadow: 'none'
            }}
            onClick={() => setShowSupportChat(true)}
          >
            <HelpCircle size={18} />
            Get Support
          </button>
        </div>
      </div>
      
      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}
      
      {/* Stat Widgets */}
      <div className="stat-widgets">
        <div className="stat-widget" onClick={onAvailableLeads} style={{ cursor: 'pointer' }}>
          <div className="stat-number">{availableUsersCount >= 100 ? '100+' : availableUsersCount}</div>
          <div className="stat-label">Available Nearby</div>
        </div>
        
        <div className="stat-widget" onClick={onInvitations} style={{ cursor: 'pointer' }}>
          <div className="stat-number">{assignedBuyersCount}</div>
          <div className="stat-label">Users Claimed</div>
        </div>
        
        <div className="stat-widget" onClick={onInvitations} style={{ cursor: 'pointer' }}>
          <div className="stat-number">{matchesSentCount}</div>
          <div className="stat-label">Matches Sent</div>
        </div>
        
        <div className="stat-widget" onClick={onInvitations} style={{ cursor: 'pointer' }}>
          <div className="stat-widget-icon">
            <Mail size={60} color="rgba(26, 26, 26, 0.15)" />
          </div>
          <div className="stat-number">{invitedUsersCount}</div>
          <div className="stat-label">Invited Users</div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="main-content-grid">
        {/* Left Column - Newest Users */}
        <div className="recent-properties">
          <h2 className="section-title">
            Newest Users
          </h2>
          
          {isLoadingAvailableUsers ? (
            <table className="properties-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Tier</th>
                  <th>Last Swiped On</th>
                  <th>Credit Score</th>
                  <th>Monthly Income</th>
                </tr>
              </thead>
              <tbody>
                {[...Array(5)].map((_, i) => (
                  <tr key={`skeleton-${i}`}>
                    <td className="property-cell">
                      <div className="property-details">
                        <span className="skeleton-bar" style={{ width: '110px' }} />
                        <div style={{ height: 6 }} />
                        <span className="skeleton-bar" style={{ width: '170px', height: '11px', opacity: 0.6 }} />
                      </div>
                    </td>
                    <td>
                      <span className="skeleton-bar" style={{ width: '80px' }} />
                    </td>
                    <td>
                      <span className="skeleton-bar" style={{ width: '64px' }} />
                    </td>
                    <td>
                      <span className="skeleton-bar" style={{ width: '48px' }} />
                    </td>
                    <td>
                      <span className="skeleton-bar" style={{ width: '68px' }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : availableUsers.length > 0 ? (
            <>
            <table className="properties-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Tier</th>
                  <th>Last Swiped On</th>
                  <th>Credit Score</th>
                  <th>Monthly Income</th>
                </tr>
              </thead>
              <tbody>
                {availableUsers.map((user) => {
                  // Get tier emoji
                  const formatDisplayName = (name) => {
                    if (!name) return 'Anonymous';
                    const parts = name.trim().split(/\s+/);
                    if (parts.length === 1) return parts[0];
                    return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
                  };

                  const getTierEmoji = (tier) => {
                    switch(tier?.toLowerCase()) {
                      case 'platinum': return '💎';
                      case 'gold': return '🥇';
                      case 'silver': return '🥈';
                      case 'bronze': return '🥉';
                      case 'flex': return '⚪';
                      default: return '⚪';
                    }
                  };
                  
                  // Get tier color
                  const getTierColor = (tier) => {
                    switch(tier?.toLowerCase()) {
                      case 'platinum': return '#2563eb';
                      case 'gold': return '#b45309';
                      case 'silver': return '#64748b';
                      case 'bronze': return '#c2410c';
                      case 'flex': return '#6b7280';
                      default: return '#6b7280';
                    }
                  };
                  
                  // Rent vs Buy on the lead's most recent swipe. Prefers a
                  // denormalized field on the user; falls back to the newest
                  // populated like/dislike, which only approximates swipe order.
                  const getLastSwipeType = (lead) => {
                    const raw =
                      lead.lastSwipedListingType ||
                      lead.lastSwipedProperty?.listingType ||
                      lead.likes?.[lead.likes.length - 1]?.listingType ||
                      lead.dislikes?.[lead.dislikes.length - 1]?.listingType;

                    if (!raw) return null;
                    const v = String(raw).toLowerCase();
                    return (v.includes('rent') || v.includes('lease')) ? 'Rental' : 'Purchase';
                  };

                  // Calculate monthly income
                  const calculateMonthlyIncome = (income, frequency) => {
                    if (!income) return null;
                    switch(frequency?.toLowerCase()) {
                      case 'weekly': return income * 4.33;
                      case 'bi-weekly':
                      case 'biweekly':
                      case 'bi weekly': return income * 2.17;
                      case 'monthly':
                      default: return income;
                    }
                  };
                  
                  // Format currency
                  const formatCurrency = (amount) => {
                    if (!amount) return 'N/A';
                    return `$${Math.round(amount).toLocaleString()}`;
                  };
                  
                  const monthlyIncome = calculateMonthlyIncome(
                    user.onboardingData?.monthlyIncome,
                    user.onboardingData?.incomeFrequency
                  );
                  
                  return (
                    <tr
                      key={user._id}
                      onClick={onAvailableLeads}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="property-cell">
                        <div className="property-details">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div className="property-address" style={{ 
                              color: '#1a1a1a',
                              fontWeight: '600'
                            }}>
                              {formatDisplayName(user.name)}
                            </div>
                            {user.hasBotChat && (
                              <span
                                title="Has chatted with the Lauft assistant"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '19px',
                                  height: '19px',
                                  borderRadius: '50%',
                                  backgroundColor: '#ece9fb',
                                  flexShrink: 0,
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                                }}
                              >
                                <img
                                  src={darkIcon}
                                  alt="Bot"
                                  style={{ width: '11px', height: '11px', objectFit: 'contain' }}
                                />
                              </span>
                            )}
                            {!isDemoAccount && user.hasDeclined && (
                              <span style={{
                                backgroundColor: '#f9fafb',
                                border: '1px solid rgba(0, 0, 0, 0.08)',
                                color: '#6b7280',
                                padding: '2px 7px',
                                borderRadius: '5px',
                                fontSize: '0.65rem',
                                fontWeight: '600',
                                letterSpacing: '0.02em'
                              }}>
                                viewed + declined
                              </span>
                            )}
                          </div>
                          <div className="property-neighborhood">{user.email}</div>
                        </div>
                      </td>
                      <td>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span style={{ fontSize: '1.25rem' }}>
                            {getTierEmoji(user.leadTier)}
                          </span>
                          <span style={{
                            color: getTierColor(user.leadTier),
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            fontSize: '0.8rem'
                          }}>
                            {user.leadTier || 'Flex'}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div style={{
                          display: 'inline-block',
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          border: '1px solid rgba(0, 0, 0, 0.08)',
                          color: '#374151',
                          padding: '4px 9px',
                          borderRadius: '6px',
                          fontSize: '0.78rem',
                          fontWeight: '600'
                        }}>
                          {getLastSwipeType(user) || 'N/A'}
                        </div>
                      </td>
                      <td>
                        <div style={{
                          color: '#1a1a1a',
                          fontSize: '0.9rem',
                          fontWeight: '600'
                        }}>
                          {user.onboardingData?.creditScore || 'N/A'}
                        </div>
                      </td>
                      <td>
                        <div style={{
                          color: '#1a1a1a',
                          fontSize: '0.9rem',
                          fontWeight: '600'
                        }}>
                          {formatCurrency(monthlyIncome)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <button 
              onClick={onAvailableLeads}
              style={{
                width: '100%',
                marginTop: '16px',
                padding: '12px 24px',
                background: '#ffffff',
                border: '1px solid rgba(26, 26, 26, 0.12)',
                borderRadius: '8px',
                color: '#1a1a1a',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(253, 215, 0, 0.1)';
                e.target.style.borderColor = 'rgba(212, 168, 0, 0.4)';
                e.target.style.color = '#a87f00';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#ffffff';
                e.target.style.borderColor = 'rgba(26, 26, 26, 0.12)';
                e.target.style.color = '#1a1a1a';
              }}
            >
              View All Available Users
            </button>
            </>
          ) : (
            <div className="empty-state">
              <p>No available users at the moment.</p>
              <button onClick={onAvailableLeads} className="add-first-property-btn">
                View All Available Users
              </button>
            </div>
          )}
        </div>

        {/* Right Column - Quick Actions + Agent Info */}
        <div>
          {/* Quick Actions Panel */}
          <div className="quick-actions-panel">
            <h3 className="quick-actions-title">Quick Actions</h3>
            
            <button 
              className="quick-action-btn"
              onClick={onInvitations}
            >
              <PlusCircle size={20} />
              Invite New User
            </button>
            
            <button 
              className="quick-action-btn"
              onClick={onShowings}
            >
              <Calendar size={20} />
              View Assigned Showings
            </button>
          </div>

          {/* Compact Agent Info */}
          <div className="agent-info-compact">
            <div 
              className="agent-info-header"
              onClick={() => setShowPasswordModal(true)}
              style={{ cursor: 'pointer' }}
            >
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1a1a1a', margin: 0, letterSpacing: '-0.01em' }}>
                Agent Information
              </h3>
              <Lock size={18} color="rgba(26, 26, 26, 0.4)" />
            </div>
            
            <div className="agent-info-expanded">
              <div className="agent-info-item">
                <span className="agent-info-label">Name</span>
                <span className="agent-info-value">{agentData?.name || agentInfo?.name || 'Agent'}</span>
              </div>
              
              <div className="agent-info-item">
                <span className="agent-info-label">Email</span>
                <span className="agent-info-value">
                  {agentData?.agentInfo?.aliasEmail || agentInfo?.agentInfo?.aliasEmail || agentData?.email || agentInfo?.email || 'Not provided'}
                </span>
              </div>
              
              <div className="agent-info-item">
                <span className="agent-info-label">Phone</span>
                <span className="agent-info-value">{formatPhone(agentData?.phone || agentInfo?.phone)}</span>
              </div>
              
              <div className="agent-info-item">
                <span className="agent-info-label">Brokerage</span>
                <span className="agent-info-value">
                  {agentData?.agentInfo?.agency || agentInfo?.agentInfo?.agency || agentData?.agency || agentInfo?.agency || 'Not provided'}
                </span>
              </div>
              
              <div className="agent-info-item">
                <span className="agent-info-label">License #</span>
                <span className="agent-info-value">
                  {agentData?.agentInfo?.licenseNumber || agentInfo?.agentInfo?.licenseNumber || 'Not provided'}
                </span>
              </div>
              
              <button 
                className="quick-action-btn"
                onClick={handleEmailRequest}
                style={{ marginTop: '12px', marginBottom: 0 }}
              >
                <Mail size={16} />
                Request Information Update
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Property Details Modal */}
      <PropertyDetailsModal 
        property={selectedProperty}
        isOpen={showDetailsModal}
        onClose={closePropertyDetails}
      />

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal-content password-modal" style={{
            background: '#ffffff',
            border: '1px solid rgba(26, 26, 26, 0.08)'
          }}>
            <div className="modal-header">
              <h3 style={{ color: '#1a1a1a' }}>Change Password</h3>
              <button className="btn-close" onClick={() => setShowPasswordModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleChangePassword}>
              {passwordError && <div className="alert alert-error">{passwordError}</div>}
              {passwordSuccess && <div className="alert alert-success">{passwordSuccess}</div>}
              
              <div className="form-group">
                <label htmlFor="currentPassword" style={{ color: '#1a1a1a' }}>Current Password</label>
                <div className="password-input-container">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    id="currentPassword"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    style={{
                      background: '#ffffff',
                      border: '1px solid rgba(26, 26, 26, 0.15)',
                      color: '#1a1a1a'
                    }}
                  />
                  <button 
                    type="button" 
                    className="toggle-password-visibility"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="newPassword" style={{ color: '#1a1a1a' }}>New Password</label>
                <div className="password-input-container">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    style={{
                      background: '#ffffff',
                      border: '1px solid rgba(26, 26, 26, 0.15)',
                      color: '#1a1a1a'
                    }}
                  />
                  <button 
                    type="button" 
                    className="toggle-password-visibility"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <div className="password-requirements" style={{ color: 'rgba(26, 26, 26, 0.55)' }}>
                  Password must have at least 5 characters, include an uppercase letter and a symbol.
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword" style={{ color: '#1a1a1a' }}>Confirm New Password</label>
                <div className="password-input-container">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    style={{
                      background: '#ffffff',
                      border: '1px solid rgba(26, 26, 26, 0.15)',
                      color: '#1a1a1a'
                    }}
                  />
                  <button 
                    type="button" 
                    className="toggle-password-visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => setShowPasswordModal(false)}
                  style={{
                    background: '#ffffff',
                    border: '1px solid rgba(26, 26, 26, 0.15)',
                    color: 'rgba(26, 26, 26, 0.8)'
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={isChangingPassword}
                  style={{
                    backgroundColor: '#fdd700',
                    color: '#1a1a1a'
                  }}
                >
                  {isChangingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Photo Upload Modal */}
      {showPhotoModal && (
        <div className="modal-overlay">
          <div className="modal-content photo-upload-modal">
            <div className="modal-header">
              <h3 className="photo-modal-title">
                {showCropper ? 'Crop Your Photo' : 'Update Profile Photo'}
              </h3>
              <button className="btn-close" onClick={closePhotoModal}>
                <X size={20} />
              </button>
            </div>
            
            {photoError && <div className="alert alert-error" style={{ margin: '0 0 16px 0' }}>{photoError}</div>}
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              style={{ display: 'none' }}
            />
            
            {showCropper ? (
              /* Cropper View */
              <div className="cropper-container">
                <div className="cropper-wrapper">
                  <Cropper
                    image={imageToCrop}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    cropShape="round"
                    showGrid={false}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                </div>
                
                <div className="cropper-controls">
                  <div className="zoom-control">
                    <ZoomOut size={18} className="zoom-icon" />
                    <input
                      type="range"
                      min={1}
                      max={3}
                      step={0.1}
                      value={zoom}
                      onChange={(e) => setZoom(Number(e.target.value))}
                      className="zoom-slider"
                    />
                    <ZoomIn size={18} className="zoom-icon" />
                  </div>
                </div>
                
                <div className="photo-actions" style={{ justifyContent: 'flex-end' }}>
                  <button 
                    type="button" 
                    className="btn-cancel photo-modal-cancel"
                    onClick={handleCropCancel}
                  >
                    Back
                  </button>
                  <button 
                    className="btn-upload"
                    onClick={handleCropConfirm}
                  >
                    Crop Photo
                  </button>
                </div>
              </div>
            ) : !photoPreview ? (
              <div
                className={`photo-drop-zone ${isDragging ? 'dragging' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <div className="photo-drop-zone-icon-wrap">
                  <Upload size={30} className="photo-drop-zone-icon" />
                </div>
                <p className="photo-drop-zone-text">
                  Drag &amp; drop your photo here
                </p>
                <p className="photo-drop-zone-subtext">
                  or click to browse
                </p>
                <span className="photo-drop-zone-hint">
                  JPG or PNG · up to 10MB
                </span>
              </div>
            ) : (
              <div className="photo-preview-container">
                <div className="photo-preview-wrap">
                  <img
                    src={photoPreview.dataUrl}
                    alt="Preview"
                    className="photo-preview"
                  />
                  <div className="photo-preview-check">
                    <Check size={18} strokeWidth={3} />
                  </div>
                </div>
                <button
                  className="btn-change-photo"
                  onClick={() => {
                    setPhotoPreview(null);
                    fileInputRef.current?.click();
                  }}
                >
                  Choose a different photo
                </button>
              </div>
            )}
            
            {!showCropper && (
              <div className="photo-actions" style={{ justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  className="btn-cancel photo-modal-cancel"
                  onClick={closePhotoModal}
                >
                  Cancel
                </button>
                <button 
                  className="btn-upload"
                  onClick={handlePhotoUpload}
                  disabled={!photoPreview || isUploadingPhoto}
                >
                  {isUploadingPhoto ? (
                    <>
                      <span className="spinner spinner-light" style={{ width: '16px', height: '16px' }}></span>
                      Uploading...
                    </>
                  ) : (
                    'Save Photo'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* ===== Cancelled / inactive account lockout =====
          When the agent's account status is 'inactive' (the end state for a
          cancelled account), block the entire portal with a non-dismissable
          overlay. Portaled to document.body so it escapes any parent layout
          stacking context and reliably covers the sidebar/header too. */}
      {(agentData?.agentStatus || agentInfo?.agentStatus) === 'inactive' && !lockoutDismissed && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2147483647,
            background: 'rgba(15, 17, 15, 0.72)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              padding: '36px 32px',
              maxWidth: '420px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 24px 70px rgba(0, 0, 0, 0.45)',
              border: '1px solid rgba(42, 45, 42, 0.08)',
            }}
          >
            <div
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'rgba(122, 148, 113, 0.14)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 18px auto',
              }}
            >
              <Lock size={26} color="#7a9471" />
            </div>
            <h2 style={{ margin: '0 0 10px 0', fontSize: '1.35rem', fontWeight: 700, color: '#2a2d2a' }}>
              Your account has been canceled
            </h2>
            <p style={{ margin: '0 0 26px 0', fontSize: '0.95rem', lineHeight: 1.55, color: '#6b7463' }}>
              This account has been canceled is no longer active. Please feel free to reactivate your account or reach out to our team with additional questions or for additional support.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                type="button"
                onClick={handleReactivateAccount}
                style={{
                  width: '100%',
                  padding: '13px 16px',
                  background: '#7a9471',
                  border: '1px solid #7a9471',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  boxShadow: '0 2px 8px rgba(122, 148, 113, 0.35)',
                }}
              >
                Reactivate Account
              </button>
              <button
                type="button"
                onClick={handleCancelledSupport}
                style={{
                  width: '100%',
                  padding: '13px 16px',
                  background: '#ffffff',
                  border: '1px solid rgba(122, 148, 113, 0.4)',
                  borderRadius: '12px',
                  color: '#5d7456',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Get Support
              </button>
            </div>
            <span
              role="button"
              tabIndex={0}
              onClick={() => setLockoutDismissed(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setLockoutDismissed(true); }}
              style={{
                display: 'inline-block',
                marginTop: '18px',
                fontSize: '0.85rem',
                fontWeight: 500,
                color: '#9aa39a',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                cursor: 'pointer',
              }}
            >
              Access account to export existing user data
            </span>
          </div>
        </div>,
        document.body
      )}

      {dashboardContent}
      
      {/* Lead Claim Modal */}
      {showLeadModal && selectedLeadId && (
        <LeadClaimModal
          userId={selectedLeadId}
          onClose={() => {
            setShowLeadModal(false);
            setSelectedLeadId(null);
          }}
          onClaimed={() => {
            // Refresh agent data to update points
            fetchAgentData();
          }}
        />
      )}
      
      {/* Points Purchase Modal */}
      {showPointsModal && (
        <PointsPurchaseModal
          onClose={() => setShowPointsModal(false)}
        />
      )}
      
      {/* Support Chat Widget */}
      <SupportChat
        isOpen={showSupportChat}
        onClose={() => setShowSupportChat(false)}
      />
    </>
  );
};

export default AgentDashboard;